import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import AuthServices from 'core/authentication/authService';
import { appInsights } from 'components/ApplicationInsights';

const PORTAL_RETRY_HEADER = 'X-Portal-Retry';
const authService = new AuthServices();

const portalAxiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_PORTAL_API_BASE_URL,
  timeout: 8000,
});

portalAxiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const creds = authService.credentials();
    if (creds?.access_token) {
      config.headers = config.headers ?? {};
      config.headers['Authorization'] = `Bearer ${creds.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

portalAxiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<{ code?: string; message?: string }>) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const originalRequest = error.config as InternalAxiosRequestConfig & { [PORTAL_RETRY_HEADER]?: boolean };

    if (status === 403 && code === 'USER_DISABLED') {
      appInsights.trackEvent({ name: 'Portal_User_Disabled' });
      window.dispatchEvent(
        new CustomEvent('portal:user-disabled', {
          detail: { message: 'Usuário sem permissão de acesso.' },
        })
      );
      return Promise.reject(error);
    }

    if (status === 503 && code === 'SERVICE_UNAVAILABLE') {
      appInsights.trackEvent({ name: 'Portal_Service_Unavailable' });
      window.dispatchEvent(
        new CustomEvent('portal:service-unavailable', {
          detail: {
            message:
              error.response?.data?.message ||
              'Serviço temporariamente indisponível. Tente novamente em instantes.',
          },
        })
      );
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest[PORTAL_RETRY_HEADER]) {
      originalRequest[PORTAL_RETRY_HEADER] = true;
      try {
        await authService.refreshToken();
        const creds = authService.credentials();
        if (creds?.access_token) {
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers['Authorization'] = `Bearer ${creds.access_token}`;
        }
        return portalAxiosInstance(originalRequest);
      } catch {
        appInsights.trackEvent({ name: 'Portal_Session_Expired' });
        authService.cleanup();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default portalAxiosInstance;
